import React, { useEffect, useState } from 'react';
import '../CSS/Global.css';
import { useSidebar } from '../SidebarContext';
import Header from './Header';
import Sidebar from './SideBar';
import { toast } from 'react-toastify';

/** Dev: same-origin `/publication-about` → CRA proxy → http://localhost:5000 (see package.json "proxy"). */
function getPublicationAboutApiBase() {
    const explicit = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
    if (explicit) return `${explicit}/publication-about`;
    if (process.env.NODE_ENV === 'development') return '/publication-about';
    return 'https://backend.impactjournal.co.in/publication-about';
}

const API_BASE = getPublicationAboutApiBase();

const EMPTY_FORM = {
    journal_title: '',
    starting_year: '',
    frequency: '',
    format: '',
    subject: '',
    language: '',
    publisher_name: '',
    publisher_address: '',
    email: '',
    website: '',
};

const FIELDS = [
    { key: 'journal_title', label: 'Journal Title', rows: 2 },
    { key: 'starting_year', label: 'Starting Year', rows: 1 },
    { key: 'frequency', label: 'Frequency', rows: 1 },
    { key: 'format', label: 'Format', rows: 1 },
    { key: 'subject', label: 'Subject', rows: 3 },
    { key: 'language', label: 'Language', rows: 1 },
    { key: 'publisher_name', label: 'Publisher Name', rows: 2 },
    { key: 'publisher_address', label: 'Publisher Address', rows: 4 },
    { key: 'email', label: 'Email', rows: 2 },
    { key: 'website', label: 'Website', rows: 1 },
];

const AboutPublication = () => {
    const { isSidebarOpen } = useSidebar();
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(API_BASE);
                if (!res.ok) throw new Error('fetch failed');
                const data = await res.json();
                setForm({
                    journal_title: data.journal_title ?? '',
                    starting_year: data.starting_year ?? '',
                    frequency: data.frequency ?? '',
                    format: data.format ?? '',
                    subject: data.subject ?? '',
                    language: data.language ?? '',
                    publisher_name: data.publisher_name ?? '',
                    publisher_address: data.publisher_address ?? '',
                    email: data.email ?? '',
                    website: data.website ?? '',
                });
            } catch {
                toast.error('Failed to load publication details');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleChange = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        void (async () => {
            setSaving(true);
            try {
                const res = await fetch(API_BASE, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form),
                });
                const body = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(body.message || 'Save failed');
                toast.success(body.message || 'Saved successfully');
            } catch (err) {
                const msg = err?.message || 'Failed to save';
                const hint =
                    msg === 'Failed to fetch'
                        ? ' Start the Backend (port 5000) or set REACT_APP_API_URL. Try disabling extensions that block requests.'
                        : '';
                toast.error(`${msg}${hint}`);
            } finally {
                setSaving(false);
            }
        })().catch(() => {
            /* prevent CRA error overlay if anything still rejects */
        });
    };

    return (
        <>
            <Header />
            <Sidebar />
            <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="oms-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginBottom: 16 }}>
                    <h1 className="dashboard-title">About the Publication</h1>
                    <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>
                        Edit details shown on the public &quot;About the Publication&quot; page.
                    </p>
                </div>
                <div style={{ padding: '0 24px 48px', maxWidth: 900 }}>
                    {loading ? (
                        <p>Loading…</p>
                    ) : (
                        <form onSubmit={handleSubmit} className="about-pub-admin-form">
                            {FIELDS.map(({ key, label, rows }) => (
                                <label key={key} className="about-pub-admin-field">
                                    <span>{label}</span>
                                    <textarea
                                        value={form[key]}
                                        onChange={handleChange(key)}
                                        rows={rows}
                                    />
                                </label>
                            ))}
                            <button type="submit" className="oms-add-btn" disabled={saving} style={{ alignSelf: 'flex-start' }}>
                                {saving ? 'Saving…' : 'Save changes'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

export default AboutPublication;
